import type { ReactNode } from 'react';

/**
 * **Les titres du produit, sur une seule famille.**
 *
 * `DESIGN.md` : « **la hiérarchie tient sur les graisses et les tailles.** 800 pour ce qui
 * titre, nomme ou compte, 700 pour une action et un statut, 500 pour le corps. **Aucun
 * titre n'a besoin d'une autre famille.** » Un serif de titrage a été **écarté en cours
 * d'entretien**, jugé incohérent avec la sans : le rouvrir ici serait redécider la charte.
 *
 * **Le niveau visuel et le niveau de titre sont deux choses**, et les séparer évite le
 * choix entre une page mal hiérarchisée et une page mal lue : `niveau` porte la structure
 * du document, `taille` porte le poids visuel.
 */
type Props = {
  readonly niveau: 1 | 2 | 3;
  readonly taille?: '30' | '24' | '20' | '18';
  readonly children: ReactNode;
};

const PAR_TAILLE = {
  '30': 'text-30',
  '24': 'text-24',
  '20': 'text-20',
  '18': 'text-18',
} as const;

/**
 * **La balise se lit dans une table, elle ne se fabrique pas par concaténation.**
 *
 * `` `h${niveau}` `` a l'air équivalent et ne l'est pas : TypeScript en tire une union de
 * trois balises, et **JSX intersecte alors les propriétés des trois**, ce qui fait perdre
 * `children`. Le correcteur automatique du lint retire de surcroît l'assertion qui
 * rattrapait le coup, en la jugeant inutile — donc la contourner par une assertion ne
 * tient pas dans le temps. Une table tient.
 */
const PAR_NIVEAU = { 1: 'h1', 2: 'h2', 3: 'h3' } as const;

export function Titre({ niveau, taille, children }: Props) {
  const Balise = PAR_NIVEAU[niveau];
  const parDefaut = niveau === 1 ? '30' : niveau === 2 ? '24' : '20';
  return (
    <Balise
      className={[
        PAR_TAILLE[taille ?? parDefaut],
        'font-weight-800 leading-serre tracking-titre text-texte-fort',
      ].join(' ')}
    >
      {children}
    </Balise>
  );
}
